import { Box, createStyles, Flex, Group, UnstyledButton } from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import type { FormikProps } from 'formik';
import { AddGlossarySynonym } from '../../InlineGlossarySynonyms/AddGlossarySynonym';
import { GlossarySynonymPill } from '../../InlineGlossarySynonyms/GlossarySynonymPill';
import type { TemplateFormik } from './ManageTemplatePage';

const useStyles = createStyles((theme) => ({
	wrapper: {
		flex: '1 !important',
	},
	button: {
		cursor: 'pointer',
		paddingLeft: theme.spacing.sm,
		paddingRight: theme.spacing.sm,
		paddingTop: theme.spacing['3xs'],
		paddingBottom: theme.spacing['3xs'],
		borderRadius: theme.other.borderRadius.sm,
		'&:hover': {
			backgroundColor: theme.other.getColor('surface/secondary/hover'),
		},
	},
}));

export interface SynonymsTemplateSelectorProps {
	formik: FormikProps<TemplateFormik>;
}

export function SynonymsTemplateSelector({
	formik,
}: SynonymsTemplateSelectorProps) {
	const { classes, theme } = useStyles();

	return (
		<Group spacing="xs" noWrap align="flex-start">
			<Text w={120} size="sm" py="4xs" color="text/secondary/default">
				Synonyms
			</Text>
			<Group spacing="xs" className={classes.wrapper}>
				{formik.values.synonyms.length > 0 && (
					// other PropertySelector components display selected items
					// as buttons with padding. This box helps align the synonyms
					<Box w={theme.spacing.xs} />
				)}
				{formik.values.synonyms.map((synonym: string) => (
					<GlossarySynonymPill
						key={`${synonym}`}
						synonym={synonym}
						onRemove={async () => {
							formik.setFieldValue(
								'synonyms',
								formik.values.synonyms.filter((s: string) => s !== synonym)
							);
						}}
					/>
				))}
				{formik.values.synonyms.length > 0 && (
					<AddGlossarySynonym
						onAdd={async (synonym: string) => {
							formik.setFieldValue('synonyms', [
								...formik.values.synonyms,
								synonym,
							]);
						}}
					/>
				)}
				{formik.values.synonyms.length === 0 && (
					<AddGlossarySynonym
						onAdd={async (synonym: string) => {
							formik.setFieldValue('synonyms', [
								...formik.values.synonyms,
								synonym,
							]);
						}}
					>
						<UnstyledButton className={classes.button}>
							<Flex align="center" gap="3xs">
								<Icon name="plus" color="text/secondary/default" />
								<Text size="sm" color="text/secondary/default">
									Add synonym
								</Text>
							</Flex>
						</UnstyledButton>
					</AddGlossarySynonym>
				)}
			</Group>
		</Group>
	);
}
